'use client';
import { SearchIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import { searchClient } from '@util/getTypesense';
import { useSearchBoots } from '@util/hooks/useSearchBoots';
import { isMobile } from '@util/index';
import { ProductDocument } from 'models/product';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import {
  Configure,
  InstantSearch,
  useHits,
  useSearchBox,
} from 'react-instantsearch-hooks-web';
import { SearchClient } from 'typesense-instantsearch-adapter';

export default function SearchBar() {
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    showSearchPreview,
    setShowSearchPreview,
  } = useSearchBoots();

  const router = useRouter();

  const handleSearchBoots = () => {
    router.push(
      `/shop/riding-gear/dirt-bike-and-motocross-boots?keyword=${searchTerm}`
    );
  };

  return (
    <div className="relative flex h-auto w-[45rem] items-center justify-between rounded-full bg-white py-2 pl-8 pr-2 sm:w-[73rem]">
      <input
        type="text"
        name="search"
        id="search"
        placeholder="Search all boots"
        className="h-8 w-full rounded-full border-none px-4 py-2 text-[1.8rem] ring-0"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowSearchPreview(!!e.target.value);
        }}
        onKeyDown={(ev) => {
          if (ev.key == 'Enter') {
            const keyword = (ev.target as HTMLInputElement).value;

            if (keyword) {
              handleSearchBoots();
              setShowSearchPreview(false);
            }
          }
        }}
      />

      <Link
        href={`/shop/riding-gear/dirt-bike-and-motocross-boots?keyword=${searchTerm}`}
        aria-label="Search"
        className="h-[5rem] w-[5.3rem] items-center justify-center rounded-full bg-brand-secondary p-5"
      >
        <SearchIcon className="h-full w-full text-white" />
      </Link>
      {showSearchPreview && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-[2rem] bg-white shadow-lg">
          <InstantSearch
            indexName="data_depot"
            searchClient={searchClient as SearchClient}
          >
            <Configure
              // @ts-ignore FIXME: not sure why hitsPerPage throws type error... see https://discourse.algolia.com/t/property-hitsperpage-does-not-exist-for-configure-hitsperpage/18231
              hitsPerPage={isMobile() ? 3 : 5}
              filters={'category1:Boots'}
            />
            <Suggestions
              searchTerm={debouncedSearchTerm}
              setShowSearchPreview={setShowSearchPreview}
            />
          </InstantSearch>
        </div>
      )}
    </div>
  );
}

const Suggestions = ({
  searchTerm,
  setShowSearchPreview,
}: {
  searchTerm: string;
  setShowSearchPreview: (val: boolean) => void;
}) => {
  const { hits } = useHits<ProductDocument>();
  const { refine } = useSearchBox();
  useEffect(() => {
    refine(searchTerm);
  }, [searchTerm]);
  return hits.length ? (
    <div className="flex flex-col gap-y-4 p-[2rem]">
      {hits.map((suggestion) => (
        <Link
          key={suggestion.title}
          type="button"
          aria-label="Search"
          className="flex w-full items-center gap-x-4 rounded-[1rem] p-4 text-left text-[1.8rem] text-zinc-600 hover:bg-gray-100"
          href={`/shop/riding-gear/dirt-bike-and-motocross-boots?keyword=${suggestion.title}`}
          onClick={() => {
            setShowSearchPreview(false);
          }}
        >
          <SafeImage
            src={suggestion.thumbnail}
            alt={suggestion.title}
            width={100}
            height={100}
            className="flex h-[3rem] w-[3rem] rounded-[0.5rem]"
          />

          {suggestion.title.split('/')[0]}
        </Link>
      ))}
    </div>
  ) : (
    <div className="flex h-[20rem] w-full items-center justify-center">
      <span className="text-[1.8rem] text-zinc-500">No boots found.</span>
    </div>
  );
};
